import { Resources, useResource } from "Infrastructure/Translations/Resources";
import {
  Box,
  Divider,
  IconButton,
  MenuItem,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import type { Editor } from "@tiptap/react";
import {
  Bold,
  ChevronDown,
  ChevronUp,
  IndentDecrease,
  IndentIncrease,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  Underline,
} from "lucide-react";
import { type FC, type ReactNode, useMemo, useState } from "react";

interface RichTextToolbarProps {
  editor: Editor | null;
}

export const RichTextToolbar: FC<RichTextToolbarProps> = ({ editor }) => {
  const [selectOpen, isSelectOpen] = useState(false);
  const { colors, customSpacing, radius, customFontWeight } = useTheme();
  const { t } = useResource();

  const blockTypes = useMemo(
    () => [
      {
        label: t(Resources.Components.RichTextToolbar.Body),
        value: "paragraph",
        level: undefined,
      },
      ...[1, 2, 3, 4, 5, 6].map((level) => ({
        label: t(
          Resources.Components.RichTextToolbar[
            `Heading${level}` as keyof typeof Resources.Components.RichTextToolbar
          ],
        ),
        value: "heading",
        level,
      })),
    ],
    [t],
  );

  const getCurrentBlockType = (): string => {
    if (!editor) {
      return "paragraph";
    }
    const { $from } = editor.state.selection;
    const node = $from.node($from.depth);
    if (node.type.name === "heading") {
      return `heading-${node.attrs.level}`;
    }
    return node.type.name;
  };

  const ToolbarButton: FC<{
    icon: ReactNode;
    onClick: () => void;
    title: string;
    isActive?: boolean;
  }> = ({ icon, onClick, title, isActive = false }) => (
    <IconButton
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      title={title}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 32,
        height: 32,
        borderRadius: radius.sm,
        background: isActive ? colors.background.hover : "transparent",
        color: colors.text.primary,
        transition: "all 0.2s",
        "&:hover": {
          background: colors.background.hover,
        },
        padding: 0,
      }}
      size="small"
    >
      {icon}
    </IconButton>
  );

  return (
    <Box style={{ width: "100%", overflow: "visible" }}>
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
          padding: `${customSpacing.xs} ${customSpacing.md}`,
          background: colors.background.primary,
        }}
      >
        <Select
          variant="standard"
          disableUnderline
          size="small"
          value={getCurrentBlockType()}
          onChange={(e) => {
            const val = e.target.value as string;
            if (val.startsWith("heading")) {
              const level = Number(val.split("-")[1]) as 1 | 2 | 3 | 4 | 5 | 6;
              editor?.chain().focus().setHeading({ level }).run();
            } else {
              editor?.chain().focus().setParagraph().run();
            }
          }}
          displayEmpty
          sx={{
            color: colors.text.secondary,
            background: "transparent",
            boxShadow: "none",
            minWidth: 80,
            maxWidth: 80,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            ".MuiOutlinedInput-notchedOutline": { border: 0 },
            "&:hover .MuiOutlinedInput-notchedOutline": { border: 0 },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: 0 },
            padding: 0,
            height: 32,
          }}
          inputProps={{
            sx: {
              padding: 0,
              height: 32,
              minWidth: 0,
              width: "auto",
              display: "flex",
              alignItems: "center",
            },
          }}
          IconComponent={() => null}
          open={selectOpen}
          onOpen={() => isSelectOpen(true)}
          onClose={() => isSelectOpen(false)}
          renderValue={() => {
            const selected =
              blockTypes.find(
                (b) =>
                  (b.value === "heading" ? `heading-${b.level}` : b.value) ===
                  getCurrentBlockType(),
              ) || blockTypes[blockTypes.length - 1];
            return (
              <Box style={{ display: "flex", alignItems: "center", gap: 0 }}>
                <Typography
                  variant="body3"
                  color={colors.text.secondary}
                  fontWeight={customFontWeight.medium}
                >
                  {selected.label}
                </Typography>
                {selectOpen ? (
                  <ChevronUp size={16} style={{ marginLeft: 2 }} />
                ) : (
                  <ChevronDown size={16} style={{ marginLeft: 2 }} />
                )}
              </Box>
            );
          }}
        >
          {blockTypes.map((type) => (
            <MenuItem
              key={type.label}
              value={
                type.value === "heading" ? `heading-${type.level}` : type.value
              }
            >
              {type.label}
            </MenuItem>
          ))}
        </Select>

        <ToolbarButton
          icon={<Bold size={16} color={colors.text.secondary} />}
          onClick={() => editor?.chain().focus().toggleBold().run()}
          title={t(Resources.Components.RichTextToolbar.Bold)}
          isActive={editor?.isActive("bold")}
        />
        <ToolbarButton
          icon={<Italic size={16} color={colors.text.secondary} />}
          onClick={() => editor?.chain().focus().toggleItalic().run()}
          title={t(Resources.Components.RichTextToolbar.Italic)}
          isActive={editor?.isActive("italic")}
        />
        <ToolbarButton
          icon={<Underline size={16} color={colors.text.secondary} />}
          onClick={() => editor?.chain().focus().toggleUnderline().run()}
          title={t(Resources.Components.RichTextToolbar.Underline)}
          isActive={editor?.isActive("underline")}
        />

        <Divider
          orientation="vertical"
          flexItem
          sx={{
            height: 18,
            alignSelf: "center",
          }}
        />

        <ToolbarButton
          icon={<List size={16} color={colors.text.secondary} />}
          onClick={() => editor?.chain().focus().toggleBulletList().run()}
          title={t(Resources.Components.RichTextToolbar.BulletList)}
          isActive={editor?.isActive("bulletList")}
        />
        <ToolbarButton
          icon={<ListOrdered size={16} color={colors.text.secondary} />}
          onClick={() => editor?.chain().focus().toggleOrderedList().run()}
          title={t(Resources.Components.RichTextToolbar.OrderedList)}
          isActive={editor?.isActive("orderedList")}
        />
        <ToolbarButton
          icon={<IndentDecrease size={16} color={colors.text.secondary} />}
          onClick={() => editor?.chain().focus().liftListItem("listItem").run()}
          title={t(Resources.Components.RichTextToolbar.DecreaseIndent)}
        />
        <ToolbarButton
          icon={<IndentIncrease size={16} color={colors.text.secondary} />}
          onClick={() => editor?.chain().focus().sinkListItem("listItem").run()}
          title={t(Resources.Components.RichTextToolbar.IncreaseIndent)}
        />

        <Divider
          orientation="vertical"
          flexItem
          sx={{
            height: 18,
            alignSelf: "center",
          }}
        />

        <ToolbarButton
          icon={<LinkIcon size={16} color={colors.text.secondary} />}
          onClick={() => {
            if (!editor) {
              return;
            }
            const { from, to } = editor.state.selection;
            if (from === to) {
              alert(t(Resources.Components.RichTextToolbar.LinkNoSelection));
              return;
            }
            const url = window.prompt(
              t(Resources.Components.RichTextToolbar.EnterURL),
            );
            if (url) {
              const normalizedUrl = /^(http|https):\/\//i.test(url)
                ? url
                : `https://${url}`;
              editor
                .chain()
                .focus()
                .extendMarkRange("link")
                .setLink({ href: normalizedUrl })
                .setTextSelection(to)
                .unsetMark("link")
                .run();
            }
          }}
          title={t(Resources.Components.RichTextToolbar.InsertLink)}
          isActive={editor?.isActive("link")}
        />
      </Box>
    </Box>
  );
};
